<template>
	<div id="projectHub">
		<page-header title="项目管理" :count="total" unit="个">
			<template #actions>
				<el-button type="primary" size="small" icon="el-icon-plus" @click="showCreateDialog">创建项目</el-button>
			</template>
			<el-select v-model="meetingTypeFilter" placeholder="会议类型" size="small" clearable @change="onFilterChange">
				<el-option label="全部类型" value="" />
				<el-option label="系列会" value="1" />
				<el-option label="单场会" value="0" />
			</el-select>
			<el-select v-model="statusFilter" placeholder="状态" size="small" clearable @change="onFilterChange">
				<el-option label="全部状态" value="" />
				<el-option label="进行中" value="UNDERWAY" />
				<el-option label="已结束" value="TIMEOUT" />
			</el-select>
			<source-filter v-model="sourceFilter" @change="onFilterChange" />
			<el-input v-model="searchKeyword" class="ph-search" placeholder="搜索项目..." prefix-icon="el-icon-search"
				clearable size="small" @input="debouncedSearch" />
		</page-header>

		<!-- 项目列表 -->
		<div class="project-list" v-loading="loading">
			<div class="project-row" v-for="item in projectList" :key="item.id" @click="enterProject(item)">
				<!-- 第一行：类型 + 项目名 + 状态 + 时间 -->
				<div class="row-main">
					<span class="type-tag" :class="item.meetingType === '1' ? 'series' : 'single'">
						{{ item.meetingType === '1' ? '系列会' : '单场会' }}
					</span>
					<h3 class="project-name">
						{{ item.projectName }}
						<source-badge :source="item.source" />
					</h3>
					<span class="status-dot" :class="isActive(item) ? 'active' : 'ended'"></span>
					<span class="status-text" :class="isActive(item) ? 'active' : 'ended'">
						{{ isActive(item) ? '进行中' : '已结束' }}
					</span>
					<span class="date-range">{{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}</span>
				</div>
				<!-- 第二行：会议场次 + 进入按钮 -->
				<div class="row-sub">
					<div class="meeting-stat">
						<i class="el-icon-video-camera"></i>
						<span>共 <b>{{ totalCount(item) }}</b> 场</span>
						<span class="stat-sep">·</span>
						<span>已开 <b>{{ heldCount(item) }}</b> 场</span>
						<!-- 优先级 1: 有未开会议时只显示未开提示 -->
						<span v-if="pendingCount(item) > 0" class="pending-stat">
							<i class="el-icon-time"></i>
							<b>{{ pendingCount(item) }}</b> 场未开
						</span>
						<!-- 优先级 2: 全部已开但有未结算 -->
						<span v-else-if="unsettledCount(item) > 0" class="unsettled-stat">
							<i class="el-icon-warning-outline"></i>
							<b>{{ unsettledCount(item) }}</b> 场未结算
						</span>
						<!-- 优先级 3: 全部已开且全部已结算 -->
						<span v-else-if="heldCount(item) > 0" class="all-done">·全部已结算</span>
					</div>
						<div class="enter-btn" @click.stop="enterProject(item)">
						进入项目 <i class="el-icon-arrow-right"></i>
					</div>
				</div>
			</div>

			<!-- 空状态 -->
			<div class="empty-state" v-if="!loading && !projectList.length">
				<i class="el-icon-folder-opened"></i>
				<p>暂无项目</p>
			</div>
		</div>

		<!-- 分页 -->
		<div class="app-pagination" v-if="total > size">
			<el-pagination background layout="prev, pager, next, total" :total="total" :page-size="size"
				:current-page.sync="page" @current-change="loadProjects" />
		</div>

		<!-- 创建项目弹窗 -->
		<el-dialog title="创建项目" :visible.sync="createDialogVisible" width="680px"
			:close-on-click-modal="false" custom-class="create-project-dialog compact-form-dialog">
			<el-form :model="createForm" :rules="createRules" ref="createForm"
				label-position="right" label-width="100px" size="small">
				<div class="form-section-title">项目基本信息</div>
				<el-form-item label="项目名称" prop="projectName">
					<el-input v-model="createForm.projectName" placeholder="例如：2025年肺癌靠向治疗新进展巡讲" />
				</el-form-item>
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="会议类型" prop="meetingType">
							<el-radio-group v-model="createForm.meetingType">
								<el-radio label="1">系列会</el-radio>
								<el-radio label="0">单场会</el-radio>
							</el-radio-group>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="中标金额（元）">
							<el-input v-model="createForm.totalPrice" placeholder="选填" type="number" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-form-item label="项目周期" prop="dateRange">
					<el-date-picker v-model="createForm.dateRange" type="daterange"
						range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期"
						style="width:100%" value-format="timestamp" />
				</el-form-item>
				<el-form-item label="立项单位" prop="orgId">
					<div style="display:flex;gap:8px;">
						<el-select v-model="createForm.orgId" filterable
							placeholder="输入名称搜索机构" :loading="orgSearchLoading"
							value-key="id" style="flex:1" @change="onOrgSelect">
							<el-option v-for="org in orgOptions" :key="org.id" :label="org.orgName" :value="org.id">
								<span>{{ org.orgName }}</span>
								<span style="float:right;color:#8f9bb3;font-size:11px;">{{ getOrgTypeLabel(org.orgType) }}</span>
							</el-option>
						</el-select>
						<el-button size="small" type="success" icon="el-icon-plus"
							@click="showAddOrgDialog">新增</el-button>
					</div>
				</el-form-item>

				<div class="form-section-title">人员信息</div>
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="助教手机号" prop="actuatorPhone">
							<el-input v-model="createForm.actuatorPhone" placeholder="自动创建账号并开通权限" />
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="执行机构">
							<el-input v-model="createForm.actuatorName" placeholder="助教所属公司（选填）" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="官员姓名">
							<el-input v-model="createForm.projectManager" placeholder="选填" />
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="官员手机号">
							<el-input v-model="createForm.officialPhone" placeholder="填了自动创建官员账号" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-form-item label="结算负责人">
					<el-select v-model="createForm.clearingBuyerId" filterable remote reserve-keyword
						placeholder="输入姓名/手机号搜索（官员/管理员/助教）"
						:remote-method="searchClearingBuyers" :loading="buyerSearchLoading"
						style="width:100%" clearable @change="onClearingBuyerSelect">
						<el-option v-for="u in buyerOptions" :key="u.id"
							:label="u.realName || u.phone" :value="u.id">
							<span>{{ u.realName || '未命名' }}</span>
							<span style="float:right;color:#8f9bb3;font-size:11px;">
								{{ buyerRoleLabel(u.userType) }} · {{ u.phone || '' }}
							</span>
						</el-option>
					</el-select>
				</el-form-item>
			</el-form>
			<div class="create-hint">
				<i class="el-icon-info"></i>
				提交后系统将自动创建助教/官员账号、关联机构并开通权限
			</div>
			<span slot="footer">
				<el-button @click="createDialogVisible = false">取消</el-button>
				<el-button type="primary" :loading="createLoading" @click="submitCreate">创建项目</el-button>
			</span>
		</el-dialog>

		<!-- 新增机构弹窗 -->
		<el-dialog title="新增立项单位" :visible.sync="addOrgDialogVisible" width="460px"
			:close-on-click-modal="false" append-to-body custom-class="compact-form-dialog">
			<el-form :model="addOrgForm" ref="addOrgForm" label-position="right"
				label-width="90px" size="small">
				<el-form-item label="机构名称" :rules="[{required:true,message:'请输入机构名称',trigger:'blur'}]" prop="orgName">
					<el-input v-model="addOrgForm.orgName" placeholder="例如：天安基金会" />
				</el-form-item>
				<el-form-item label="机构类型">
					<el-select v-model="addOrgForm.orgType" style="width:100%">
						<el-option label="基金会" value="FOUNDATION" />
						<el-option label="医院" value="HOSPITAL" />
						<el-option label="药企" value="PHARMA" />
						<el-option label="CRO/执行机构" value="CRO" />
						<el-option label="其他" value="OTHER" />
					</el-select>
				</el-form-item>
				<el-form-item label="联系人">
					<el-input v-model="addOrgForm.contactName" placeholder="选填" />
				</el-form-item>
				<el-form-item label="联系电话">
					<el-input v-model="addOrgForm.contactPhone" placeholder="选填" />
				</el-form-item>
			</el-form>
			<span slot="footer">
				<el-button @click="addOrgDialogVisible = false">取消</el-button>
				<el-button type="primary" :loading="addOrgLoading" @click="submitAddOrg">创建机构</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import { projectList } from '@/request/api.js';
import baseUrl from '@/request/config.js';
import axios from 'axios';
import SourceBadge from '@/components/SourceBadge.vue';
import SourceFilter from '@/components/SourceFilter.vue';
import PageHeader from '@/components/PageHeader.vue';
import { getOrgListCached, setCached } from '@/utils/optionsCache.js';

export default {
	name: 'ProjectHub',
	components: { SourceBadge, SourceFilter, PageHeader },
	data() {
		return {
			loading: false,
			meetingTypeFilter: '',
			statusFilter: '',
			searchKeyword: '',
			sourceFilter: '',
			page: 1,
			size: 10,
			total: 0,
			projectList: [],
			searchTimer: null,
			// 创建项目
			createDialogVisible: false,
			createLoading: false,
			createForm: this.emptyCreateForm(),
			createRules: {
				projectName: [{ required: true, message: '请输入项目名称', trigger: 'blur' }],
				meetingType: [{ required: true, message: '请选择会议类型', trigger: 'change' }],
				dateRange: [{ required: true, message: '请选择项目周期', trigger: 'change' }],
				orgId: [{ required: true, message: '请选择立项单位', trigger: 'change' }],
				actuatorPhone: [
					{ required: true, message: '请输入助教手机号', trigger: 'blur' },
					{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
				]
			},
			// 机构搜索 (使用 localStorage 缓存, 不再走远程搜索)
			orgOptions: [],
			orgSearchLoading: false,
			// 新增机构
			addOrgDialogVisible: false,
			addOrgLoading: false,
			addOrgForm: { orgName: '', orgType: 'FOUNDATION', contactName: '', contactPhone: '' },
			// 结算负责人 (远程搜索: 官员/管理员/助教)
			buyerOptions: [],
			buyerSearchLoading: false,
			buyerSearchTimer: null,
		}
	},
	created() {
		this.loadProjects();
	},
	watch: {
		'$store.state.sponsorId': {
			handler() {
				setTimeout(() => { this.loadProjects(); }, 300);
			},
			immediate: false
		}
	},
	methods: {
		loadProjects() {
			this.loading = true;
			let data = {
				current: this.page,
				size: this.size,
			};
			if (this.statusFilter) data.projectStatus = this.statusFilter;
			if (this.searchKeyword) data.projectName = this.searchKeyword;
			if (this.meetingTypeFilter) data.meetingType = this.meetingTypeFilter;
			if (this.sourceFilter) data.source = this.sourceFilter;

			projectList(data).then(res => {
				if (res.code === 200) {
					this.projectList = res.data.records || [];
					this.total = res.data.total || 0;
				}
				this.loading = false;
			}).catch(() => { this.loading = false; });
		},

		onFilterChange() {
			this.page = 1;
			this.loadProjects();
		},

		debouncedSearch() {
			if (this.searchTimer) clearTimeout(this.searchTimer);
			this.searchTimer = setTimeout(() => {
				this.page = 1;
				this.loadProjects();
			}, 400);
		},

		isActive(item) {
			return item.status === 'UNDERWAY' || item.status === 'ACTIVE';
		},

		// 总场次 (后端 meetingCount; 兜底用 held + pending)
		totalCount(item) {
			if (item.meetingCount != null) return item.meetingCount;
			return (item.meetingHeldCount || 0) + (item.meetingPendingCount || 0);
		},
		// 已开会议数 (向后兼容: 若后端没返回 meetingHeldCount, 回退到旧 meetingCount)
		heldCount(item) {
			if (item.meetingHeldCount != null) return item.meetingHeldCount;
			// 老接口兜底: total - pending
			const total = item.meetingCount || 0;
			const pending = item.meetingPendingCount || 0;
			return Math.max(0, total - pending);
		},
		pendingCount(item) {
			return item.meetingPendingCount || 0;
		},
		// 未结算 = 已开 - 已结算 (后端 meetingUnsettledCount 直接给, 兜底自己算)
		unsettledCount(item) {
			if (item.meetingUnsettledCount != null) return item.meetingUnsettledCount;
			const held = this.heldCount(item);
			const settled = item.meetingSettledCount || 0;
			return Math.max(0, held - settled);
		},

		formatDate(date) {
			if (!date) return '-';
			return this.$moment(date).format('YYYY/MM');
		},

		enterProject(item) {
			localStorage.setItem('projectDetails', JSON.stringify(item));
			this.$router.push({ name: 'projectDetail', query: { pId: item.id } });
		},

		// ===== 创建项目 =====
		emptyCreateForm() {
			return {
				projectName: '', meetingType: '1', dateRange: null,
				orgId: null, mechanismName: '',
				actuatorPhone: '', actuatorName: '',
				projectManager: '', officialPhone: '', totalPrice: '',
				clearingBuyerId: null, clearingBuyerName: ''
			};
		},
		async showCreateDialog() {
			this.createForm = this.emptyCreateForm();
			this.buyerOptions = [];
			this.createDialogVisible = true;
			// 从 localStorage 缓存读取机构列表 (4h TTL, 登录时预热)
			await this.loadOrgsFromCache();
			this.$nextTick(() => { this.$refs.createForm && this.$refs.createForm.clearValidate(); });
		},
		/** 从缓存读取机构列表; 缓存 miss 则自动回源并写入 */
		async loadOrgsFromCache() {
			this.orgSearchLoading = true;
			try {
				const list = await getOrgListCached();
				this.orgOptions = Array.isArray(list) ? list : [];
			} catch (e) {
				console.warn('加载机构列表失败:', e);
				this.orgOptions = [];
			}
			this.orgSearchLoading = false;
		},
		/** 选中机构时同步名称 */
		onOrgSelect(orgId) {
			const org = this.orgOptions.find(o => o.id === orgId);
			if (org) {
				this.createForm.mechanismName = org.orgName;
			}
		},
		/** 机构类型显示文字 */
		getOrgTypeLabel(type) {
			const map = { FOUNDATION: '基金会', HOSPITAL: '医院', PHARMA: '药企', CRO: 'CRO', OTHER: '其他' };
			return map[type] || type || '';
		},
		/** 新增机构弹窗 */
		showAddOrgDialog() {
			this.addOrgForm = { orgName: '', orgType: 'FOUNDATION', contactName: '', contactPhone: '' };
			this.addOrgDialogVisible = true;
		},
		/** 提交新增机构 */
		async submitAddOrg() {
			if (!this.addOrgForm.orgName.trim()) {
				this.$message.warning('请输入机构名称');
				return;
			}
			this.addOrgLoading = true;
			try {
				const token = JSON.parse(localStorage.getItem('token') || '{}');
				const res = await axios.post(`${baseUrl}/api/org/create`, this.addOrgForm, {
					headers: { token: token.token }
				});
				if (res.data?.code === 200) {
					const newOrg = res.data.data;
					this.$message.success(`机构「${newOrg.orgName}」创建成功`);
					this.addOrgDialogVisible = false;
					// 自动选中新创建的机构
					this.orgOptions.unshift(newOrg);
					this.createForm.orgId = newOrg.id;
					this.createForm.mechanismName = newOrg.orgName;
					// 同步刷新 localStorage 缓存, 让其他入口立刻看到
					setCached('orgList', this.orgOptions);
				} else {
					this.$message.error(res.data?.msg || '创建失败');
				}
			} catch (e) {
				this.$message.error('创建失败：' + e.message);
			}
			this.addOrgLoading = false;
		},
		/** 远程搜索结算负责人 (官员 / 管理员 / 助教) - 实时性强不缓存 */
		searchClearingBuyers(query) {
			if (this.buyerSearchTimer) clearTimeout(this.buyerSearchTimer);
			this.buyerSearchTimer = setTimeout(() => this.fetchClearingBuyers(query), 300);
		},
		async fetchClearingBuyers(query) {
			this.buyerSearchLoading = true;
			try {
				const token = JSON.parse(localStorage.getItem('token') || '{}');
				// 并发查 3 个角色, 合并结果
				const roles = ['OFFICIAL', 'ADMIN', 'ASSISTANT'];
				const reqs = roles.map(r => axios.get(`${baseUrl}/api/user/list`, {
					params: { userType: r, keyword: query || '', page: 1, size: 20 },
					headers: { token: token.token }
				}).catch(() => null));
				const results = await Promise.all(reqs);
				const merged = [];
				results.forEach(res => {
					const records = res?.data?.data?.records || [];
					records.forEach(u => merged.push(u));
				});
				this.buyerOptions = merged;
			} catch (e) {
				console.warn('搜索结算负责人失败:', e);
				this.buyerOptions = [];
			}
			this.buyerSearchLoading = false;
		},
		onClearingBuyerSelect(userId) {
			if (!userId) {
				this.createForm.clearingBuyerName = '';
				return;
			}
			const u = this.buyerOptions.find(x => x.id === userId);
			if (u) this.createForm.clearingBuyerName = u.realName || u.phone || '';
		},
		buyerRoleLabel(t) {
			const map = { OFFICIAL: '官员', ADMIN: '管理员', ASSISTANT: '助教' };
			return map[t] || t || '';
		},
		submitCreate() {
			this.$refs.createForm.validate(async valid => {
				if (!valid) return;
				this.createLoading = true;
				const f = this.createForm;
				const token = JSON.parse(localStorage.getItem('token') || '{}');
				try {
					const res = await axios.post(`${baseUrl}/api/project/create`, {
						projectName: f.projectName,
						meetingType: f.meetingType,
						orgId: f.orgId,
						mechanismName: f.mechanismName,
						actuatorPhone: f.actuatorPhone,
						actuatorName: f.actuatorName || undefined,
						projectManager: f.projectManager || undefined,
						officialPhone: f.officialPhone || undefined,
						totalPrice: f.totalPrice || undefined,
						clearingBuyerId: f.clearingBuyerId || undefined,
						clearingBuyerName: f.clearingBuyerName || undefined,
						startDate: f.dateRange ? f.dateRange[0] : null,
						endDate: f.dateRange ? f.dateRange[1] : null
					}, { headers: { token: token.token } });

					if (res.data?.code === 200) {
						this.$message.success('项目创建成功，账号已自动开通');
						this.createDialogVisible = false;
						const newProject = res.data.data;
						// 跳转到新项目详情
						localStorage.setItem('projectDetails', JSON.stringify(newProject));
						this.$router.push({ name: 'projectDetail', query: { pId: newProject.id } });
					} else {
						this.$message.error(res.data?.msg || '创建失败');
					}
				} catch (e) {
					this.$message.error('创建失败：' + e.message);
				} finally {
					this.createLoading = false;
				}
			});
		}
	}
}
</script>

<style lang="scss" scoped>
#projectHub {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	padding: 32px 40px;
	background: #f0f2f8;

	/* .page-header 已抽到 PageHeader 组件 */

	.project-list {
		flex: 1;
	}

	.project-row {
		background: #fff;
		border-radius: 10px;
		padding: 10px 16px;
		margin-bottom: 6px;
		cursor: pointer;
		border: 1px solid #eef0f6;
		transition: all 0.2s ease;

		&:hover {
			border-color: #667eea;
			box-shadow: 0 2px 8px rgba(102, 126, 234, 0.08);
		}

		.row-main {
			display: flex;
			align-items: center;
			gap: 10px;
			margin-bottom: 4px;

			.type-tag {
				font-size: 11px;
				font-weight: 700;
				padding: 3px 10px;
				border-radius: 6px;
				flex-shrink: 0;

				&.series {
					background: rgba(5, 150, 105, 0.08);
					color: #059669;
				}
				&.single {
					background: rgba(99, 102, 241, 0.08);
					color: #6366f1;
				}
			}

			.project-name {
				font-size: 15px;
				font-weight: 600;
				color: #1a1a2e;
				margin: 0;
				flex: 1;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			.status-dot {
				width: 6px;
				height: 6px;
				border-radius: 50%;
				flex-shrink: 0;
				&.active { background: #059669; }
				&.ended { background: #a8a29e; }
			}

			.status-text {
				font-size: 12px;
				font-weight: 600;
				flex-shrink: 0;
				&.active { color: #059669; }
				&.ended { color: #a8a29e; }
			}

			.date-range {
				font-size: 12px;
				color: #8f9bb3;
				flex-shrink: 0;
				margin-left: auto;
			}
		}

		.row-sub {
			display: flex;
			align-items: center;
			justify-content: space-between;

			.meeting-stat {
				display: flex;
				align-items: center;
				gap: 6px;
				font-size: 13px;
				color: #8f9bb3;

				i { font-size: 14px; }
				b { color: #1a1a2e; font-weight: 700; }
				.stat-sep { color: #d0d7de; margin: 0 2px; }

				.pending-stat {
					display: inline-flex;
					align-items: center;
					gap: 4px;
					margin-left: 6px;
					padding: 1px 8px;
					border-radius: 10px;
					background: rgba(230, 162, 60, 0.12);
					color: #d48806;
					font-size: 12px;
					b { color: #d48806; }
				}
				/* 未结算: 比未开更"警示"用红色 pill (因为会议已开完但钱没结) */
				.unsettled-stat {
					display: inline-flex;
					align-items: center;
					gap: 4px;
					margin-left: 6px;
					padding: 1px 8px;
					border-radius: 10px;
					background: rgba(245, 108, 108, 0.12);
					color: #cf1322;
					font-size: 12px;
					b { color: #cf1322; }
				}
				.all-done {
					margin-left: 6px;
					color: #67c23a;
					font-size: 12px;
				}
			}

			.enter-btn {
				font-size: 13px;
				font-weight: 600;
				color: #667eea;
				display: flex;
				align-items: center;
				gap: 4px;
				padding: 4px 12px;
				border-radius: 8px;
				transition: all 0.2s;

				&:hover {
					background: rgba(102, 126, 234, 0.08);
				}

				i { font-size: 12px; }
			}
		}
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 80px 20px;
		color: #a8a29e;

		i { font-size: 48px; margin-bottom: 12px; }
		p { font-size: 14px; }
	}

	.pagination {
		margin: 20px auto;
		text-align: center;
	}

	// 创建项目弹窗
	.form-section-title {
		font-size: 13px;
		font-weight: 700;
		color: #667eea;
		margin: 16px 0 12px;
		padding-bottom: 6px;
		border-bottom: 1px solid #f0f2f8;
		&:first-child { margin-top: 0; }
	}

	.create-hint {
		background: #f0f2f8;
		border-radius: 8px;
		padding: 10px 14px;
		font-size: 12px;
		color: #8f9bb3;
		margin-top: 8px;
		i {
			color: #667eea;
			margin-right: 6px;
		}
	}
}
</style>
